import {BaseCommand} from './base.command';
import {SmapStyleSnapshot} from '../interfaces/command.interface';
import {MapService} from '../../../api/services/map.service';
import {CytoscapeService} from '../../cy-services/cytoscape.service';
import {firstValueFrom} from 'rxjs';
import {getPrimaryStyleName, UndoLabelMetadata} from '../undo-label.utils';
import {LayoutType} from '../../../api/interfaces/graph-node.interface';

/**
 * SmapStyleCommand handles map-level style changes.
 * Includes: background color, image, sound, appearance settings, layout, etc.
 */
export class SmapStyleCommand extends BaseCommand<SmapStyleSnapshot> {
  constructor(
    private mapService: MapService,
    snapshot: SmapStyleSnapshot,
    private cyService?: CytoscapeService
  ) {
    // Get the primary style name for the part
    // Use _changedStyles if available (only contains actually changed properties)
    // Otherwise fall back to styles object
    const changedStyles = (snapshot.afterStyles as any)._changedStyles;
    const innerStyles = changedStyles && Object.keys(changedStyles).length > 0
      ? changedStyles
      : (snapshot.afterStyles as any).styles || snapshot.afterStyles;
    const styleName = getPrimaryStyleName(innerStyles, 'smap');

    const labelMetadata: UndoLabelMetadata = {
      object: 'Smap',
      verb: 'Change',
      part: styleName || 'Style'
    };

    super(
      'smap-style',
      `Smap ${styleName || 'Style'} Change`,
      {before: snapshot, after: snapshot},
      labelMetadata
    );
  }

  async execute(): Promise<void> {
    const {mapId, afterStyles} = this.snapshot.after;

    // Filter out internal properties
    const {_changedStyles, ...stylesToApply} = afterStyles as any;

    // Get current map and merge the changes
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    if (!currentMap || currentMap.id !== mapId) return;

    // Apply changes to current map
    if (stylesToApply.automaticLayout !== undefined) {
      currentMap.automaticLayout = stylesToApply.automaticLayout;
    }
    if (stylesToApply.styles !== undefined) {
      currentMap.styles = stylesToApply.styles;
    }

    // Send full map to backend (like dropdown does)
    await firstValueFrom(this.mapService.update(currentMap));

    // Update local store
    this.mapService.setCurrentSelectedMapToStore({...currentMap});

    // If layout changed, trigger the layout signal
    if (stylesToApply.automaticLayout !== undefined && this.cyService) {
      this.cyService.setLayoutChangedSignal(stylesToApply.automaticLayout as LayoutType);
    }
  }

  async undo(): Promise<void> {
    const {mapId, beforeStyles} = this.snapshot.before;

    // Filter out internal properties
    const {_changedStyles, ...stylesToApply} = beforeStyles as any;

    // Get current map and merge the changes
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    if (!currentMap || currentMap.id !== mapId) return;

    // Apply changes to current map
    if (stylesToApply.automaticLayout !== undefined) {
      currentMap.automaticLayout = stylesToApply.automaticLayout;
    }
    if (stylesToApply.styles !== undefined) {
      currentMap.styles = stylesToApply.styles;
    }

    // Send full map to backend (like dropdown does)
    await firstValueFrom(this.mapService.update(currentMap));

    // Update local store
    this.mapService.setCurrentSelectedMapToStore({...currentMap});

    // If layout changed, trigger the layout signal
    if (stylesToApply.automaticLayout !== undefined && this.cyService) {
      this.cyService.setLayoutChangedSignal(stylesToApply.automaticLayout as LayoutType);
    }
  }
}
